import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Printer, Download, FilePdf } from "@phosphor-icons/react";

export default function QRPage() {
  const [data, setData] = useState(null);
  const [dlPoster, setDlPoster] = useState(false);

  useEffect(() => { api.get("/vendor/qr").then(r => setData(r.data)); }, []);

  const copyUrl = async () => {
    if (!data) return;
    await navigator.clipboard.writeText(data.url);
    toast.success("Link copied");
  };
  const download = () => {
    if (!data) return;
    const a = document.createElement("a");
    a.href = data.data_url;
    a.download = `vendorly-${data.slug}.png`;
    a.click();
  };
  const print = () => window.print();
  const downloadPoster = async () => {
    setDlPoster(true);
    try {
      const resp = await api.get("/vendor/qr-poster", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data?.slug || "vendorly"}-poster.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Poster downloaded — print on A5");
    } catch {
      toast.error("Could not generate poster");
    } finally { setDlPoster(false); }
  };

  return (
    <div data-testid="qr-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-2">Your counter QR</div>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Print. Stick. Collect.</h1>
        <p className="text-brand-text/70 mt-1">Customers scan this to join your list. Everyone lands on your branded form.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-brand-border rounded-3xl p-8 flex flex-col items-center justify-center text-center print:border-0" data-testid="qr-card">
          {data ? (
            <>
              <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-3">Scan to join</div>
              <div className="p-4 bg-brand-bg rounded-2xl border border-brand-border">
                <img src={data.data_url} alt="QR" className="w-64 h-64" data-testid="qr-image" />
              </div>
              <div className="mt-6 font-mono text-sm text-brand-text/70 break-all max-w-md">{data.url}</div>
            </>
          ) : (
            <div className="text-brand-textMuted py-24">Generating…</div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-white border-2 border-brand-primary rounded-2xl p-6 relative overflow-hidden" data-testid="poster-card">
            <div className="absolute top-3 right-3 text-[10px] uppercase tracking-widest bg-brand-primary text-white px-2 py-1 rounded-full font-bold">Recommended</div>
            <div className="flex items-center gap-2 mb-2">
              <FilePdf size={22} weight="duotone" className="text-brand-primary" />
              <h3 className="font-heading text-xl font-bold">Ready-to-print A5 poster</h3>
            </div>
            <p className="text-sm text-brand-text/75 mb-4">
              Full-color, bilingual (English + Hindi). Fits on any home printer. Stick it on your counter and forget about it.
            </p>
            <button onClick={downloadPoster} disabled={dlPoster || !data} data-testid="download-poster"
              className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold transition-colors disabled:opacity-60">
              <FilePdf size={20} weight="duotone" /> {dlPoster ? "Building your poster…" : "Download poster (PDF)"}
            </button>
          </div>

          <div className="bg-white border border-brand-border rounded-2xl p-6">
            <h3 className="font-heading text-xl font-bold mb-3">Share your form</h3>
            <p className="text-sm text-brand-text/70 mb-4">Prefer to send a direct link over WhatsApp or Instagram bio? Copy it here.</p>
            <div className="flex gap-2">
              <input readOnly value={data?.url || ""} data-testid="form-url"
                className="flex-1 h-11 px-3 rounded-xl bg-brand-bg border border-brand-border font-mono text-xs" />
              <button onClick={copyUrl} data-testid="copy-url"
                className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-brand-secondary hover:bg-brand-secondaryHover text-white text-sm font-semibold transition-colors">
                <Copy size={16} weight="duotone" /> Copy
              </button>
            </div>
          </div>

          <div className="bg-white border border-brand-border rounded-2xl p-6">
            <h3 className="font-heading text-xl font-bold mb-3">More options</h3>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={download} data-testid="download-qr"
                className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl border border-brand-border hover:border-brand-borderHover font-semibold transition-colors">
                <Download size={18} weight="duotone" /> QR only (PNG)
              </button>
              <button onClick={print} data-testid="print-qr"
                className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl border border-brand-border hover:border-brand-borderHover font-semibold transition-colors">
                <Printer size={18} weight="duotone" /> Print this page
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
